import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Brain, LayoutDashboard, Briefcase, Megaphone, BarChart3, FileText, 
  LogOut, ChevronLeft, ChevronRight, ClipboardList, Users, Settings,
  ClipboardCheck, Award, TrendingUp, Mail, Clock, GripVertical, ChevronDown,
  DollarSign, Receipt, Wallet, PiggyBank, ArrowLeftRight, Package, UserCog, 
  CreditCard, PhoneCall, RotateCcw, Sparkles, Heart, Share2, PieChart
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import api from '../utils/api';

// Definicion de modulos con permisos por rol y area
const ALL_MENU_ITEMS = [
  { 
    icon: LayoutDashboard, 
    label: 'Dashboard', 
    path: '/dashboard',
    roles: ['Administrador', 'Líder', 'Coordinador', 'Usuario'],
    areas: null
  },
  { 
    icon: ClipboardCheck, 
    label: 'Mis Tareas Pendientes', 
    path: '/mis-tareas',
    roles: ['Administrador', 'Líder', 'Coordinador', 'Usuario'],
    areas: null
  },
  { 
    icon: Award, 
    label: 'Mis Tareas Realizadas', 
    path: '/mis-tareas-realizadas',
    roles: ['Administrador', 'Líder', 'Coordinador', 'Usuario'],
    areas: null
  },
  { 
    icon: Clock, 
    label: 'Tareas en Revision', 
    path: '/tareas-en-revision',
    roles: ['Administrador', 'Líder', 'Coordinador', 'Usuario'],
    areas: null
  },
  { 
    icon: Brain, 
    label: 'Cerebro de Tareas', 
    path: '/cerebro',
    roles: ['Administrador', 'Líder'],
    areas: null
  },
  { 
    icon: ClipboardList, 
    label: 'Tablero Lideres', 
    path: '/tablero-lideres',
    roles: ['Administrador', 'Líder'],
    areas: null
  },
  { 
    icon: Users, 
    label: 'Contacto Clientes', 
    path: '/contacto-clientes',
    roles: ['Administrador', 'Líder', 'Coordinador', 'Usuario'],
    areas: ['Comercial', 'Tributaria', 'Legal', 'Contabilidad', 'Adm. y Finanzas']
  },
  { 
    icon: Briefcase, 
    label: 'Comercial y Marketing', 
    path: '/comercial',
    roles: ['Administrador', 'Líder', 'Coordinador', 'Usuario'],
    areas: null,
    expandable: true
  },
  { 
    icon: TrendingUp, 
    label: 'Oportunidades', 
    path: '/oportunidades',
    roles: ['Administrador', 'Líder', 'Coordinador', 'Usuario'],
    areas: null
  },
  { 
    icon: Users, 
    label: 'Master de Clientes', 
    path: '/master-clientes',
    roles: ['Administrador', 'Líder'],
    areas: ['Comercial', 'Socios']
  },
  { 
    icon: BarChart3, 
    label: 'Finanzas', 
    path: '/finanzas',
    roles: ['Administrador', 'Líder'],
    areas: ['Adm. y Finanzas', 'Socios']
  },
  { 
    icon: FileText, 
    label: 'Informes para Clientes', 
    path: '/informes',
    roles: ['Administrador', 'Líder', 'Coordinador'],
    areas: null
  },
  { 
    icon: BarChart3, 
    label: 'KPIs', 
    path: '/kpis',
    roles: ['Administrador', 'Líder'],
    areas: ['Socios', 'Adm. y Finanzas']
  },
  { 
    icon: TrendingUp, 
    label: 'Reporte de Tareas', 
    path: '/reporte-tareas',
    roles: ['Administrador'],
    areas: ['Socios']
  },
  { 
    icon: FileText, 
    label: 'Plantillas PDF', 
    path: '/plantillas',
    roles: ['Administrador'],
    areas: null
  },
  { 
    icon: Settings, 
    label: 'Panel de Control', 
    path: '/panel-control',
    roles: ['Administrador'],
    areas: null
  },
  { 
    icon: Mail, 
    label: 'Config. Notificaciones', 
    path: '/config-notificaciones',
    roles: ['Administrador'],
    areas: null
  },
];

// Sub-modulos de Finanzas
const FINANZAS_SUBMODULES = [
  { id: 'ingresos', label: 'Ingresos', icon: DollarSign, path: '/finanzas?tab=ingresos' },
  { id: 'facturacion', label: 'Facturacion', icon: Receipt, path: '/finanzas?tab=facturacion' },
  { id: 'conciliacion', label: 'Conciliacion', icon: ArrowLeftRight, path: '/finanzas?tab=conciliacion' },
  { id: 'caja-chica', label: 'Caja Chica', icon: PiggyBank, path: '/finanzas?tab=caja-chica' },
  { id: 'flujo-caja', label: 'Flujo de Caja', icon: TrendingUp, path: '/finanzas?tab=flujo-caja' },
  { id: 'inventarios', label: 'Inventarios', icon: Package, path: '/finanzas?tab=inventarios' },
  { id: 'rrhh', label: 'RRHH', icon: UserCog, path: '/finanzas?tab=rrhh' },
  { id: 'gastos', label: 'Gastos', icon: CreditCard, path: '/finanzas?tab=gastos' },
  { id: 'cobranza', label: 'Cobranza', icon: PhoneCall, path: '/finanzas?tab=cobranza' },
  { id: 'reembolsos', label: 'Reembolsos', icon: RotateCcw, path: '/finanzas?tab=reembolsos' },
];

// Sub-modulos de Comercial y Marketing
const COMERCIAL_SUBMODULES = [
  { id: 'venta', label: 'Venta', icon: DollarSign, path: '/comercial/venta' },
  { id: 'post-venta', label: 'Post Venta', icon: Heart, path: '/comercial/post-venta' },
  { id: 'marketing', label: 'Marketing', icon: Megaphone, path: '/comercial/marketing' },
  { id: 'redes-sociales', label: 'Redes Sociales', icon: Share2, path: '/comercial/redes-sociales' },
  { id: 'reportes', label: 'Reportes', icon: PieChart, path: '/comercial/reportes' },
];

// Componente Sortable para cada item del menu
const SortableMenuItem = ({ item, isActive, collapsed, onClick, isAdmin }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.path, disabled: !isAdmin });

  const Icon = item.icon;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 'auto',
  };

  return (
    <motion.div
      ref={setNodeRef}
      style={style}
      className={`relative flex items-center ${isDragging ? 'opacity-80' : ''}`}
      data-testid={`sidebar-item-${item.label.toLowerCase().replace(/\s/g, '-')}`}
    >
      {/* Drag Handle */}
      {isAdmin && !collapsed && (
        <div
          {...attributes}
          {...listeners}
          className="absolute left-0 top-1/2 -translate-y-1/2 p-1 cursor-grab active:cursor-grabbing text-white/20 hover:text-white/40 transition-colors"
          title="Arrastrar para reordenar"
        >
          <GripVertical className="w-3 h-3" />
        </div>
      )}
      
      <motion.button
        onClick={onClick}
        whileHover={{ x: 2 }}
        whileTap={{ scale: 0.98 }}
        className={`
          w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 text-left
          ${isAdmin && !collapsed ? 'pl-6' : ''}
          ${isActive
            ? 'bg-gradient-to-r from-[#3A86FF]/20 to-transparent text-white border-l-2 border-[#00D4FF]'
            : 'text-white/60 hover:text-white hover:bg-white/5'
          }
        `}
      >
        <div className={`relative ${isActive ? 'text-[#00D4FF]' : ''}`}>
          <Icon 
            className={`w-[18px] h-[18px] flex-shrink-0 transition-all duration-200 ${
              isActive ? 'drop-shadow-[0_0_8px_rgba(0,212,255,0.6)]' : ''
            }`} 
            strokeWidth={1.5} 
          />
          {isActive && (
            <div className="absolute inset-0 blur-md bg-[#00D4FF]/30 -z-10" />
          )}
        </div>
        {!collapsed && (
          <span className="text-[13px] font-medium tracking-wide">{item.label}</span>
        )}
      </motion.button>
    </motion.div>
  );
};

// Componente para sub-modulo de Finanzas sortable
const SortableFinanzasSubmodule = ({ submodule, isActive, collapsed, onClick, isAdmin }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: submodule.id, disabled: !isAdmin });

  const Icon = submodule.icon;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 'auto',
  };

  return (
    <motion.div
      ref={setNodeRef}
      style={style}
      className={`relative flex items-center ${isDragging ? 'opacity-80' : ''}`}
      data-testid={`finanzas-submodule-${submodule.id}`}
    >
      {isAdmin && !collapsed && (
        <div
          {...attributes}
          {...listeners}
          className="absolute left-0 top-1/2 -translate-y-1/2 p-0.5 cursor-grab active:cursor-grabbing text-white/20 hover:text-white/40 transition-colors"
        >
          <GripVertical className="w-2.5 h-2.5" />
        </div>
      )}
      
      <motion.button
        onClick={onClick}
        whileHover={{ x: 2 }}
        whileTap={{ scale: 0.98 }}
        className={`
          w-full flex items-center gap-2 px-2 py-2 rounded-md transition-all text-left
          ${isAdmin && !collapsed ? 'pl-4' : 'pl-2'}
          ${isActive
            ? 'bg-[#3A86FF]/15 text-[#00D4FF]'
            : 'text-white/50 hover:text-white/80 hover:bg-white/5'
          }
        `}
      >
        <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${isActive ? 'drop-shadow-[0_0_6px_rgba(0,212,255,0.5)]' : ''}`} strokeWidth={1.5} />
        {!collapsed && <span className="text-[11px] font-medium">{submodule.label}</span>}
      </motion.button>
    </motion.div>
  );
};

// Componente especial para el menu expandible de Finanzas
const FinanzasExpandableMenu = ({ 
  item, 
  isActive, 
  collapsed, 
  isAdmin, 
  finanzasExpanded, 
  setFinanzasExpanded,
  finanzasSubOrder,
  onSubmoduleClick,
  onSubmoduleDragEnd,
  location,
  sensors,
  dragHandleProps  // top-level drag handle from parent SortableContext
}) => {
  const Icon = item.icon;
  const currentTab = new URLSearchParams(location.search).get('tab');

  // Filtrar submódulos según permisos_finanzas del usuario (si no es Admin/Líder con área Finanzas).
  // Si user tiene permisos_finanzas no vacío, mostrar SOLO los submódulos listados.
  const allowedSubmodules = useMemo(() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      const perms = u.permisos_finanzas || [];
      const role = u.role || '';
      const areas = u.areas || [];
      const esGestorTotal =
        role === 'Administrador' ||
        (role === 'Líder' && (areas.includes('Adm. y Finanzas') || areas.includes('Socios')));
      if (esGestorTotal) return FINANZAS_SUBMODULES;
      if (perms.length > 0) {
        return FINANZAS_SUBMODULES.filter(s => perms.includes(s.id));
      }
      return FINANZAS_SUBMODULES;
    } catch {
      return FINANZAS_SUBMODULES;
    }
  }, []);

  const orderedSubmodules = useMemo(() => {
    if (finanzasSubOrder.length === 0) {
      return allowedSubmodules;
    }
    return [...allowedSubmodules].sort((a, b) => {
      const indexA = finanzasSubOrder.indexOf(a.id);
      const indexB = finanzasSubOrder.indexOf(b.id);
      if (indexA === -1 && indexB === -1) return 0;
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
  }, [finanzasSubOrder, allowedSubmodules]);

  return (
    <div className="relative" data-testid="finanzas-expandable-menu">
      {dragHandleProps && isAdmin && !collapsed && (
        <div
          {...dragHandleProps}
          className="absolute left-1 top-3 z-10 cursor-grab active:cursor-grabbing text-white/20 hover:text-white/40 transition-colors"
          title="Arrastrar para reordenar"
        >
          <GripVertical className="w-3 h-3" />
        </div>
      )}
      <motion.button
        onClick={() => setFinanzasExpanded(!finanzasExpanded)}
        whileHover={{ x: 2 }}
        whileTap={{ scale: 0.98 }}
        className={`
          w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 text-left
          ${isAdmin && !collapsed ? 'pl-6' : 'pl-3'}
          ${isActive || finanzasExpanded
            ? 'bg-gradient-to-r from-[#3A86FF]/20 to-transparent text-white border-l-2 border-[#00D4FF]'
            : 'text-white/60 hover:text-white hover:bg-white/5'
          }
        `}
        data-testid="finanzas-toggle-btn"
      >
        <div className={`relative ${isActive ? 'text-[#00D4FF]' : ''}`}>
          <Icon 
            className={`w-[18px] h-[18px] flex-shrink-0 transition-all duration-200 ${
              isActive ? 'drop-shadow-[0_0_8px_rgba(0,212,255,0.6)]' : ''
            }`} 
            strokeWidth={1.5} 
          />
        </div>
        {!collapsed && (
          <>
            <span className="text-[13px] font-medium tracking-wide flex-1">{item.label}</span>
            <motion.div
              animate={{ rotate: finanzasExpanded ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <ChevronDown className="w-4 h-4 text-white/40" />
            </motion.div>
          </>
        )}
      </motion.button>

      <AnimatePresence>
        {finanzasExpanded && !collapsed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden ml-3 mt-1 border-l border-[#3A86FF]/20 pl-2"
          >
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={onSubmoduleDragEnd}
            >
              <SortableContext
                items={orderedSubmodules.map(s => s.id)}
                strategy={verticalListSortingStrategy}
              >
                {orderedSubmodules.map((submodule) => (
                  <SortableFinanzasSubmodule
                    key={submodule.id}
                    submodule={submodule}
                    isActive={location.pathname === '/finanzas' && currentTab === submodule.id}
                    collapsed={collapsed}
                    onClick={() => onSubmoduleClick(submodule)}
                    isAdmin={isAdmin}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Componente para sub-modulo de Comercial sortable
const SortableComercialSubmodule = ({ submodule, isActive, collapsed, onClick, isAdmin }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: submodule.id, disabled: !isAdmin });

  const Icon = submodule.icon;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 'auto',
  };

  return (
    <motion.div
      ref={setNodeRef}
      style={style}
      className={`relative flex items-center ${isDragging ? 'opacity-80' : ''}`}
      data-testid={`comercial-submodule-${submodule.id}`}
    >
      {isAdmin && !collapsed && (
        <div
          {...attributes}
          {...listeners}
          className="absolute left-0 top-1/2 -translate-y-1/2 p-0.5 cursor-grab active:cursor-grabbing text-white/20 hover:text-white/40 transition-colors"
        >
          <GripVertical className="w-2.5 h-2.5" />
        </div>
      )}
      <motion.button
        onClick={onClick}
        whileHover={{ x: 2 }}
        whileTap={{ scale: 0.98 }}
        className={`
          w-full flex items-center gap-2.5 px-2.5 py-2 my-0.5 rounded-md transition-all text-left
          ${isAdmin && !collapsed ? 'pl-4' : 'pl-2.5'}
          ${isActive
            ? 'bg-[#3A86FF]/30 text-white border-l-2 border-[#00D4FF]'
            : 'text-white/50 hover:text-white hover:bg-white/5'
          }
        `}
        data-testid={`comercial-sub-${submodule.id}`}
      >
        <Icon
          className={`w-[15px] h-[15px] flex-shrink-0 ${isActive ? 'text-[#00D4FF]' : ''}`}
          strokeWidth={1.5}
        />
        {!collapsed && <span className="text-[12px] font-medium">{submodule.label}</span>}
      </motion.button>
    </motion.div>
  );
};

// Componente expandable para Comercial y Marketing CON drag-and-drop
const ComercialExpandableMenu = ({
  item,
  collapsed,
  expanded,
  setExpanded,
  isAdmin,
  comercialSubOrder,
  onSubmoduleDragEnd,
  location,
  navigate,
  sensors,
  dragHandleProps  // top-level drag handle from parent SortableContext
}) => {
  const Icon = item.icon;
  const isAnyChildActive = location.pathname.startsWith('/comercial');

  const orderedSubmodules = useMemo(() => {
    if (!comercialSubOrder || comercialSubOrder.length === 0) {
      return COMERCIAL_SUBMODULES;
    }
    return [...COMERCIAL_SUBMODULES].sort((a, b) => {
      const indexA = comercialSubOrder.indexOf(a.id);
      const indexB = comercialSubOrder.indexOf(b.id);
      if (indexA === -1 && indexB === -1) return 0;
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
  }, [comercialSubOrder]);

  return (
    <div className="relative" data-testid="comercial-expandable-menu">
      {dragHandleProps && isAdmin && !collapsed && (
        <div
          {...dragHandleProps}
          className="absolute left-1 top-3 z-10 cursor-grab active:cursor-grabbing text-white/20 hover:text-white/40 transition-colors"
          title="Arrastrar para reordenar"
        >
          <GripVertical className="w-3 h-3" />
        </div>
      )}
      <motion.button
        onClick={() => setExpanded(!expanded)}
        whileHover={{ x: 2 }}
        whileTap={{ scale: 0.98 }}
        className={`
          w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 text-left
          ${isAdmin && !collapsed ? 'pl-6' : 'pl-3'}
          ${isAnyChildActive || expanded
            ? 'bg-gradient-to-r from-[#3A86FF]/20 to-transparent text-white border-l-2 border-[#00D4FF]'
            : 'text-white/60 hover:text-white hover:bg-white/5'
          }
        `}
        data-testid="comercial-toggle-btn"
      >
        <div className={`relative ${isAnyChildActive ? 'text-[#00D4FF]' : ''}`}>
          <Icon
            className={`w-[18px] h-[18px] flex-shrink-0 transition-all duration-200 ${
              isAnyChildActive ? 'drop-shadow-[0_0_8px_rgba(0,212,255,0.6)]' : ''
            }`}
            strokeWidth={1.5}
          />
        </div>
        {!collapsed && (
          <>
            <span className="text-[13px] font-medium tracking-wide flex-1">{item.label}</span>
            <motion.div animate={{ rotate: expanded ? 180 : 0 }} transition={{ duration: 0.2 }}>
              <ChevronDown className="w-4 h-4 text-white/40" />
            </motion.div>
          </>
        )}
      </motion.button>

      <AnimatePresence>
        {expanded && !collapsed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden ml-3 mt-1 border-l border-[#3A86FF]/20 pl-2"
          >
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={onSubmoduleDragEnd}
            >
              <SortableContext
                items={orderedSubmodules.map(s => s.id)}
                strategy={verticalListSortingStrategy}
              >
                {orderedSubmodules.map((sub) => (
                  <SortableComercialSubmodule
                    key={sub.id}
                    submodule={sub}
                    isActive={location.pathname === sub.path}
                    collapsed={collapsed}
                    onClick={() => navigate(sub.path)}
                    isAdmin={isAdmin}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Wrapper sortable para el item de Finanzas a nivel top (drag entre modulos)
const SortableFinanzasWrapper = (props) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: '/finanzas',
    disabled: !props.isAdmin,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 'auto',
  };
  return (
    <div ref={setNodeRef} style={style} className={isDragging ? 'opacity-80' : ''}>
      <FinanzasExpandableMenu {...props} dragHandleProps={{ ...attributes, ...listeners }} />
    </div>
  );
};

// Wrapper sortable para el item de Comercial a nivel top
const SortableComercialWrapper = (props) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: '/comercial',
    disabled: !props.isAdmin,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 'auto',
  };
  return (
    <div ref={setNodeRef} style={style} className={isDragging ? 'opacity-80' : ''}>
      <ComercialExpandableMenu {...props} dragHandleProps={{ ...attributes, ...listeners }} />
    </div>
  );
};

const Sidebar = ({ collapsed, setCollapsed }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [customOrder, setCustomOrder] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [finanzasExpanded, setFinanzasExpanded] = useState(false);
  const [comercialExpanded, setComercialExpanded] = useState(() => {
    if (typeof window === 'undefined') return false;
    const saved = localStorage.getItem('comercial_expanded');
    return saved === 'true';
  });
  
  // Persistir estado expandido en localStorage
  useEffect(() => {
    localStorage.setItem('comercial_expanded', String(comercialExpanded));
  }, [comercialExpanded]);
  
  // Auto-expandir si la ruta actual es de Comercial
  useEffect(() => {
    if (location.pathname.startsWith('/comercial')) {
      setComercialExpanded(true);
    }
  }, [location.pathname]);
  const [finanzasSubOrder, setFinanzasSubOrder] = useState([]);
  const [comercialSubOrder, setComercialSubOrder] = useState([]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch {
        setUser(null);
      }
    }
  }, []);

  useEffect(() => {
    const fetchSidebarOrder = async () => {
      try {
        const response = await api.get('/config/sidebar-order');
        if (response.data.module_order && response.data.module_order.length > 0) {
          setCustomOrder(response.data.module_order);
        }
        if (response.data.finanzas_submodule_order && response.data.finanzas_submodule_order.length > 0) {
          setFinanzasSubOrder(response.data.finanzas_submodule_order);
        }
        if (response.data.comercial_submodule_order && response.data.comercial_submodule_order.length > 0) {
          setComercialSubOrder(response.data.comercial_submodule_order);
        }
      } catch (error) {
        console.log('Using default sidebar order');
      }
    };
    fetchSidebarOrder();
  }, []);

  const isAdmin = user?.role === 'Administrador';
  
  const handleFinanzasSubmoduleClick = (submodule) => {
    navigate(submodule.path);
  };

  const handleFinanzasSubmoduleDragEnd = async (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const currentOrder = finanzasSubOrder.length > 0 
        ? finanzasSubOrder 
        : FINANZAS_SUBMODULES.map(s => s.id);
      
      const oldIndex = currentOrder.indexOf(active.id);
      const newIndex = currentOrder.indexOf(over.id);
      const newOrder = arrayMove(currentOrder, oldIndex, newIndex);
      setFinanzasSubOrder(newOrder);

      try {
        await api.put('/config/sidebar-order', { 
          module_order: customOrder,
          finanzas_submodule_order: newOrder 
        });
        toast.success('Orden de modulos de Finanzas actualizado');
      } catch (error) {
        toast.error('Error al guardar el orden');
        const response = await api.get('/config/sidebar-order');
        if (response.data.finanzas_submodule_order) {
          setFinanzasSubOrder(response.data.finanzas_submodule_order);
        }
      }
    }
  };

  const handleComercialSubmoduleDragEnd = async (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const currentOrder = comercialSubOrder.length > 0
        ? comercialSubOrder
        : COMERCIAL_SUBMODULES.map(s => s.id);

      const oldIndex = currentOrder.indexOf(active.id);
      const newIndex = currentOrder.indexOf(over.id);
      const newOrder = arrayMove(currentOrder, oldIndex, newIndex);
      setComercialSubOrder(newOrder);

      try {
        await api.put('/config/sidebar-order', {
          module_order: customOrder,
          comercial_submodule_order: newOrder
        });
        toast.success('Orden de módulos de Comercial y Marketing actualizado');
      } catch (error) {
        toast.error('Error al guardar el orden');
        const response = await api.get('/config/sidebar-order');
        if (response.data.comercial_submodule_order) {
          setComercialSubOrder(response.data.comercial_submodule_order);
        }
      }
    }
  };

  const getVisibleMenuItems = () => {
    if (!user) {
      return ALL_MENU_ITEMS.filter(item => 
        !['Panel de Control', 'Contacto Clientes'].includes(item.label)
      );
    }

    const userRole = user.role || 'Usuario';
    const userAreas = user.areas || [];
    const permisosFinanzas = user.permisos_finanzas || [];

    return ALL_MENU_ITEMS.filter(item => {
      // Excepción: acceso a /finanzas si el usuario tiene permisos_finanzas asignados
      if (item.path === '/finanzas' && permisosFinanzas.length > 0) return true;
      if (!item.roles.includes(userRole)) return false;
      if (userRole === 'Administrador') return true;
      if (!item.areas) return true;
      return userAreas.some(area => item.areas.includes(area));
    });
  };

  const menuItems = useMemo(() => {
    const visibleItems = getVisibleMenuItems();
    if (customOrder.length === 0) return visibleItems;

    return [...visibleItems].sort((a, b) => {
      const indexA = customOrder.indexOf(a.path);
      const indexB = customOrder.indexOf(b.path);
      if (indexA === -1 && indexB === -1) return 0;
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
  }, [customOrder, user]);

  const handleDragStart = () => setIsDragging(true);

  const handleDragEnd = async (event) => {
    setIsDragging(false);
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = menuItems.findIndex(item => item.path === active.id);
      const newIndex = menuItems.findIndex(item => item.path === over.id);
      const newOrder = arrayMove(menuItems, oldIndex, newIndex);
      const newOrderPaths = newOrder.map(item => item.path);
      const allPaths = ALL_MENU_ITEMS.map(item => item.path);
      const fullOrder = newOrderPaths.concat(
        allPaths.filter(path => !newOrderPaths.includes(path))
      );
      setCustomOrder(fullOrder);

      try {
        await api.put('/config/sidebar-order', { module_order: fullOrder });
        toast.success('Orden del menu actualizado');
      } catch (error) {
        toast.error('Error al guardar el orden');
        const response = await api.get('/config/sidebar-order');
        if (response.data.module_order) {
          setCustomOrder(response.data.module_order);
        }
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    toast.success('Sesion cerrada exitosamente');
    navigate('/login');
  };

  return (
    <motion.div
      initial={false}
      animate={{ width: collapsed ? 72 : 260 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className="h-screen flex flex-col relative overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, #0F1A36 0%, #0B132B 100%)',
        borderRight: '1px solid rgba(58, 134, 255, 0.15)'
      }}
      data-testid="sidebar-container"
    >
      {/* Glow effect at top */}
      <div 
        className="absolute top-0 left-0 right-0 h-40 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at top center, rgba(58, 134, 255, 0.12) 0%, transparent 70%)'
        }}
      />

      {/* Header con Logo Premium */}
      <div className="p-4 border-b border-[#3A86FF]/15 flex-shrink-0 relative z-10">
        <div className="flex items-center gap-3">
          {/* Logo Peritum - Diamante Premium con Glow */}
          <div className="w-12 h-12 flex-shrink-0 relative">
            <div className="absolute inset-0 blur-lg bg-[#00D4FF]/30 rounded-full" />
            <svg viewBox="0 0 40 40" className="w-full h-full relative z-10">
              <defs>
                <linearGradient id="peritumGradientNew" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#3A86FF" />
                  <stop offset="50%" stopColor="#00D4FF" />
                  <stop offset="100%" stopColor="#00F5FF" />
                </linearGradient>
                <filter id="glowNew">
                  <feGaussianBlur stdDeviation="1.5" result="coloredBlur"/>
                  <feMerge>
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              </defs>
              <path 
                d="M20 2 L38 20 L20 38 L2 20 Z" 
                fill="none" 
                stroke="url(#peritumGradientNew)" 
                strokeWidth="2"
                filter="url(#glowNew)"
              />
              <path 
                d="M20 8 L32 20 L20 32 L8 20 Z" 
                fill="url(#peritumGradientNew)" 
                opacity="0.3"
              />
              <path 
                d="M20 2 L20 38 M2 20 L38 20" 
                stroke="url(#peritumGradientNew)" 
                strokeWidth="0.5" 
                opacity="0.5"
              />
            </svg>
          </div>
          <motion.div
            initial={false}
            animate={{ opacity: collapsed ? 0 : 1, width: collapsed ? 0 : 'auto' }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <h1 className="text-lg font-bold text-white tracking-wider">
              PERITUM
            </h1>
            <p className="text-[9px] text-[#00D4FF]/70 tracking-[0.2em] -mt-0.5 uppercase">
              Defensa Corporativa
            </p>
          </motion.div>
        </div>
      </div>

      {/* Toggle Button */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="absolute -right-3 top-14 z-20 w-6 h-6 rounded-full flex items-center justify-center transition-all duration-200"
        style={{
          background: 'linear-gradient(135deg, #3A86FF 0%, #00D4FF 100%)',
          boxShadow: '0 0 15px rgba(0, 212, 255, 0.4)'
        }}
        data-testid="sidebar-toggle-btn"
      >
        {collapsed ? 
          <ChevronRight className="w-4 h-4 text-white" /> : 
          <ChevronLeft className="w-4 h-4 text-white" />
        }
      </button>

      {/* User Info */}
      {user && !collapsed && (
        <div className="px-4 py-3 border-b border-[#3A86FF]/15 flex-shrink-0 flex items-center justify-between relative z-10">
          <div className="flex-1 min-w-0">
            <p className="text-[10px] text-white/40 uppercase tracking-wider">Conectado como</p>
            <p className="text-sm font-semibold text-white truncate">{user.name}</p>
            <span className="text-[10px] px-2 py-0.5 bg-[#3A86FF]/20 text-[#00D4FF] rounded-full mt-1 inline-block border border-[#3A86FF]/30">
              {user.role}
            </span>
          </div>
          <motion.button
            onClick={handleLogout}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="ml-2 p-2 rounded-lg text-white/40 hover:text-red-400 hover:bg-red-500/10 transition-all border border-transparent hover:border-red-500/30"
            title="Cerrar Sesion"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.5} />
          </motion.button>
        </div>
      )}
      
      {/* Logout para modo colapsado */}
      {user && collapsed && (
        <div className="px-3 py-3 border-b border-[#3A86FF]/15 flex-shrink-0 flex justify-center">
          <motion.button
            onClick={handleLogout}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="p-2 rounded-lg text-white/40 hover:text-red-400 hover:bg-red-500/10 transition-all"
            title="Cerrar Sesion"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.5} />
          </motion.button>
        </div>
      )}

      {/* Drag hint for Admin */}
      {isAdmin && !collapsed && (
        <div className="px-4 py-2 bg-[#3A86FF]/10 border-b border-[#3A86FF]/15">
          <p className="text-[10px] text-[#00D4FF]/70 flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" />
            Arrastra para reordenar modulos
          </p>
        </div>
      )}

      {/* Menu Items */}
      <nav 
        className="flex-1 px-3 py-3 space-y-1 overflow-y-auto relative z-10"
        style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(58, 134, 255, 0.3) transparent' }}
      >
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={menuItems.map(item => item.path)}
            strategy={verticalListSortingStrategy}
          >
            {menuItems.map((item) => (
              item.label === 'Finanzas' ? (
                <SortableFinanzasWrapper
                  key={item.path}
                  item={item}
                  isActive={location.pathname === item.path}
                  collapsed={collapsed}
                  isAdmin={isAdmin}
                  finanzasExpanded={finanzasExpanded}
                  setFinanzasExpanded={setFinanzasExpanded}
                  finanzasSubOrder={finanzasSubOrder}
                  onSubmoduleClick={handleFinanzasSubmoduleClick}
                  onSubmoduleDragEnd={handleFinanzasSubmoduleDragEnd}
                  location={location}
                  sensors={sensors}
                />
              ) : item.label === 'Comercial y Marketing' ? (
                <SortableComercialWrapper
                  key={item.path}
                  item={item}
                  collapsed={collapsed}
                  expanded={comercialExpanded}
                  setExpanded={setComercialExpanded}
                  isAdmin={isAdmin}
                  comercialSubOrder={comercialSubOrder}
                  onSubmoduleDragEnd={handleComercialSubmoduleDragEnd}
                  location={location}
                  navigate={navigate}
                  sensors={sensors}
                />
              ) : (
                <SortableMenuItem
                  key={item.path}
                  item={item}
                  isActive={location.pathname === item.path}
                  collapsed={collapsed}
                  onClick={() => navigate(item.path)}
                  isAdmin={isAdmin}
                />
              )
            ))}
          </SortableContext>
        </DndContext>
      </nav>

      {/* Footer gradient fade */}
      <div 
        className="absolute bottom-0 left-0 right-0 h-16 pointer-events-none"
        style={{
          background: 'linear-gradient(to top, #0B132B 0%, transparent 100%)'
        }}
      />
    </motion.div>
  );
};

export default Sidebar;
